import { useCallback } from 'react'
import type {
  Folder,
  FolderDraft,
  TopicDraft,
  TopicProduct,
  TopicQuery,
  TopicRecord,
} from '@headline/schemas'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  foldersQuery,
  topicsQuery,
  useCreateFolder,
  useCreateTopic,
  useDeleteFolder,
  useDeleteTopic,
  useMoveTopic,
  useUpdateFolder,
  useUpdateTopic,
} from './queries'
import { DEFAULT_REASONING_MODEL } from './reasoning-models'

/**
 * Topics and folders for one sub-account, backed by the real API. This used to be a localStorage
 * store; it is now a thin facade over the query/mutation layer in queries.ts, keeping the same
 * surface the list and wizard already consume (topics/folders + add/update/delete/move/clone +
 * findLiveConflict) so nothing downstream had to change shape.
 *
 * The server owns id/createdAt/query ids and normalizes every record, so the read-time coercion the
 * localStorage version needed is gone — `topics` is exactly what the API returns.
 */

/** A blank draft for the create flow — the shape the wizard starts editing from. */
export function emptyTopicDraft(): TopicDraft {
  return {
    name: '',
    products: [],
    description: '',
    // New topics are drafts until activated from the wizard's Review step.
    status: 'draft',
    topicTagIds: [],
    news: { enabled: true, queries: [], exclusionQueries: [] },
    // New topics start with only News on — the other sources are opt-in.
    sources: {
      x: { enabled: false, queries: [] },
      linkedin: { enabled: false, queries: [] },
      broadcast: { enabled: false, queries: [] },
      podcast: { enabled: false, queries: [] },
      newsletter: { enabled: false, queries: [], outlets: [] },
    },
    reasoningFilter: {
      enabled: false,
      prompt: '',
      model: DEFAULT_REASONING_MODEL,
      mode: 'advise',
    },
  }
}

/** The draft view of a saved record — just the wizard-editable fields. Explicitly drops the
 *  server-owned fields (`id`/`createdAt`/`folderId`) so they never ride through draft state; the
 *  server re-attaches them on save. */
export function draftFromRecord(record: TopicRecord): TopicDraft {
  return {
    name: record.name,
    products: record.products,
    description: record.description,
    status: record.status,
    topicTagIds: record.topicTagIds,
    news: record.news,
    sources: record.sources,
    reasoningFilter: record.reasoningFilter,
  }
}

// ── Config fingerprint ──────────────────────────────────────────────────────────────
// A stable string identifying a topic's *configuration* — everything that decides what it fetches.
// Ignores the fields that don't change what's fetched: name, description, status, and the
// record-identity fields (id/createdAt/folderId). Query `id`s are per-instance unique, so they're
// dropped and queries are compared by content; ref/string arrays are sets, so they're sorted.
type ConfigView = Pick<TopicRecord, 'topicTagIds' | 'news' | 'sources' | 'reasoningFilter'>

function fingerprintQuery(query: TopicQuery) {
  return {
    e: query.expression.trim(),
    t: query.tagIds.toSorted(),
    // Per-query source params — undefined on queries that don't carry them, so JSON omits them.
    v: query.x?.verifiedOnly,
    a: query.x ? query.x.authors.toSorted() : undefined,
    me: query.x?.minEngagement,
    ci: query.podcast
      ? query.podcast.creatorInclusions.map((show) => show.id).toSorted()
      : undefined,
    ca: query.podcast ? query.podcast.categories.toSorted() : undefined,
  }
}
const fingerprintQueries = (queries: TopicQuery[]) => queries.map(fingerprintQuery)

function topicConfigKey(topic: ConfigView): string {
  const s = topic.sources
  return JSON.stringify({
    topicTagIds: topic.topicTagIds.toSorted(),
    news: {
      enabled: topic.news.enabled,
      q: fingerprintQueries(topic.news.queries),
      x: fingerprintQueries(topic.news.exclusionQueries),
    },
    sources: {
      x: { e: s.x.enabled, q: fingerprintQueries(s.x.queries) },
      linkedin: { e: s.linkedin.enabled, q: fingerprintQueries(s.linkedin.queries) },
      broadcast: { e: s.broadcast.enabled, q: fingerprintQueries(s.broadcast.queries) },
      podcast: { e: s.podcast.enabled, q: fingerprintQueries(s.podcast.queries) },
      newsletter: {
        e: s.newsletter.enabled,
        q: fingerprintQueries(s.newsletter.queries),
        // Outlets change what's fetched (which newsletters), so they're part of the config identity.
        o: s.newsletter.outlets.toSorted(),
      },
    },
    reasoning: {
      enabled: topic.reasoningFilter.enabled,
      prompt: topic.reasoningFilter.prompt.trim(),
      model: topic.reasoningFilter.model,
      mode: topic.reasoningFilter.mode,
    },
  })
}

const EMPTY_TOPICS: TopicRecord[] = []
const EMPTY_FOLDERS: Folder[] = []

export function useTopicStore(subaccountId: string) {
  const queryClient = useQueryClient()
  const { data: topicsData } = useQuery(topicsQuery(subaccountId))
  const { data: foldersData } = useQuery(foldersQuery(subaccountId))
  const topics = topicsData?.topics ?? EMPTY_TOPICS
  const folders = foldersData?.folders ?? EMPTY_FOLDERS

  const createTopic = useCreateTopic(subaccountId, queryClient)
  const updateTopicM = useUpdateTopic(subaccountId, queryClient)
  const deleteTopicM = useDeleteTopic(subaccountId, queryClient)
  const moveTopicM = useMoveTopic(subaccountId, queryClient)
  const createFolder = useCreateFolder(subaccountId, queryClient)
  const updateFolderM = useUpdateFolder(subaccountId, queryClient)
  const deleteFolderM = useDeleteFolder(subaccountId, queryClient)

  const addTopic = useCallback(
    async (draft: TopicDraft, folderId: string | null = null): Promise<TopicRecord> => {
      const created = await createTopic.mutateAsync(draft)
      if (folderId) await moveTopicM.mutateAsync({ topicId: created.id, folderId })
      return created
    },
    [createTopic, moveTopicM],
  )

  const updateTopic = useCallback(
    (id: string, draft: TopicDraft) => updateTopicM.mutateAsync({ topicId: id, draft }),
    [updateTopicM],
  )

  const deleteTopic = useCallback((id: string) => deleteTopicM.mutate(id), [deleteTopicM])

  // Duplicate a topic's settings into a fresh record in the same folder. A copy starts paused —
  // you review it before it runs, even when cloned from a live topic.
  const cloneTopic = useCallback(
    async (id: string): Promise<TopicRecord | null> => {
      const source = topics.find((topic) => topic.id === id)
      if (!source) return null
      const copy = await createTopic.mutateAsync({
        ...draftFromRecord(source),
        name: `${source.name} (copy)`,
        status: 'draft',
      })
      if (source.folderId) {
        await moveTopicM.mutateAsync({ topicId: copy.id, folderId: source.folderId })
      }
      return copy
    },
    [topics, createTopic, moveTopicM],
  )

  const moveTopic = useCallback(
    (id: string, folderId: string | null) => moveTopicM.mutate({ topicId: id, folderId }),
    [moveTopicM],
  )

  const addFolder = useCallback(
    (draft: FolderDraft): Promise<Folder> => createFolder.mutateAsync(draft),
    [createFolder],
  )

  const updateFolder = useCallback(
    (id: string, draft: FolderDraft) => updateFolderM.mutate({ folderId: id, draft }),
    [updateFolderM],
  )

  const deleteFolder = useCallback((id: string) => deleteFolderM.mutate(id), [deleteFolderM])

  // Another *live* topic with the same configuration (excluding name), if any. Used to enforce
  // "no two identical topics live at once" — a draft/other-status match doesn't count, and the
  // topic being edited is excluded via `excludeId`.
  const findLiveConflict = useCallback(
    (draft: TopicDraft, excludeId?: string): TopicRecord | null => {
      const key = topicConfigKey(draft)
      return (
        topics.find(
          (topic) =>
            topic.status === 'live' && topic.id !== excludeId && topicConfigKey(topic) === key,
        ) ?? null
      )
    },
    [topics],
  )

  return {
    topics,
    addTopic,
    updateTopic,
    deleteTopic,
    cloneTopic,
    moveTopic,
    findLiveConflict,
    folders,
    addFolder,
    updateFolder,
    deleteFolder,
  }
}

/**
 * Every picker offers every topic (CLI-744) — the ones this product already uses first, then the
 * rest, each group in its original order. The products a topic carries are a label, never a filter.
 */
export function usedFirst<T extends { products: TopicProduct[] }>(
  topics: T[],
  product: TopicProduct,
): T[] {
  return [
    ...topics.filter((topic) => topic.products.includes(product)),
    ...topics.filter((topic) => !topic.products.includes(product)),
  ]
}
